// Thin dApp shim over the @tonado/core RelayerRegistry helpers.
//
// Provides:
//   - `discoverRelayers()`: list all active bonded relayers from the
//     on-chain registry, hydrated with their advertised metadata
//     (endpoint URL, fee BPS, etc.). Falls back to a one-element list
//     containing the configured RELAYER_URL when no registry is set.
//   - `RelayerEndpoint`: the user-facing shape the UI displays.
//
// UI integration: the existing Withdraw component reads from a single
// RELAYER_URL today. When the dApp gains a relayer-picker dropdown, it
// should call `discoverRelayers()` and let the user choose. Until then,
// this module is dormant capability — built and exported but not
// consumed by the UI.

import { Address } from "@ton/core";
import { RELAYER_STATUS, type RegisteredRelayer } from "@tonado/core";
import { RELAYER_REGISTRY_ADDRESS, RELAYER_URL } from "./env";
import { listActiveRelayersViaRelayer } from "./relayerRpc";

export interface RelayerMetadata {
  /** HTTPS endpoint that speaks the relayer REST API. */
  endpoint: string;
  /**
   * Optional: the TON address (friendly base64url) of the wallet this
   * relayer uses to bond + submit withdrawals. When omitted, the dApp
   * reconstructs from the on-chain accountHash assuming workchain=0
   * (basechain — the norm for user wallets).
   */
  tonAddress?: string;
  /** Display name. Optional; falls back to the endpoint hostname. */
  name?: string;
  /** Fee in basis points the relayer charges. Optional; the actual fee
   *  is authoritative from the relayer's /status endpoint. */
  feeBps?: number;
  /** Free-form region / description hints. */
  region?: string;
  description?: string;
}

export interface RelayerEndpoint {
  /** The on-chain bond record, or `null` if this is the fallback
   *  RELAYER_URL not present in the registry. */
  registered: RegisteredRelayer | null;
  metadata: RelayerMetadata;
}

/**
 * If the registry is configured, returns every active relayer with
 * parsed metadata. Always includes the configured RELAYER_URL as a
 * fallback first-entry when (a) no registry is set, or (b) the registry
 * has zero active relayers — so the UI never shows an empty list.
 *
 * Relayers without a `metadataUri` are skipped — there's nothing the
 * dApp can do with a bonded relayer it can't reach. They show up via
 * the raw `listActiveRelayers` SDK call if a caller wants them.
 */
export async function discoverRelayers(): Promise<RelayerEndpoint[]> {
  const fallback: RelayerEndpoint = {
    registered: null,
    metadata: { endpoint: RELAYER_URL },
  };

  if (!RELAYER_REGISTRY_ADDRESS) {
    return [fallback];
  }

  try {
    // Validate format up front so we fail soft (single-relayer fallback)
    // rather than letting the relayer's /registry endpoint throw.
    Address.parse(RELAYER_REGISTRY_ADDRESS);
  } catch {
    console.warn(
      `Invalid VITE_RELAYER_REGISTRY_ADDRESS (${RELAYER_REGISTRY_ADDRESS}); falling back to single-relayer mode`
    );
    return [fallback];
  }

  // List active relayers through the relayer's /registry/active-relayers
  // endpoint — server-side toncenter, so the dApp ships no API key.
  // The relayer's response includes both the friendly address and the
  // raw on-chain fields the rest of this module expects.
  const rows = await listActiveRelayersViaRelayer(RELAYER_REGISTRY_ADDRESS);

  const endpoints: RelayerEndpoint[] = [];
  for (const r of rows) {
    if (!r.metadataUri) continue; // skip relayers without resolvable metadata
    const metadata = await fetchRelayerMetadata(r.metadataUri).catch(() => null);
    if (!metadata) continue;
    // Reconstruct the legacy `RegisteredRelayer` shape that the rest of
    // the dApp consumes (notably resolveRelayerAddress below).
    const registered: RegisteredRelayer = {
      accountHash: BigInt(r.accountHash),
      status:
        r.status === "ACTIVE"
          ? RELAYER_STATUS.ACTIVE
          : r.status === "UNBONDING"
            ? RELAYER_STATUS.UNBONDING
            : RELAYER_STATUS.NOT_REGISTERED,
      bondedAt: r.bondedAt,
      unbondAt: r.unbondAt,
      metadataUri: null, // decoded URI lives in `metadata.endpoint` already
    };
    endpoints.push({ registered, metadata });
  }

  if (endpoints.length === 0) {
    return [fallback];
  }
  return endpoints;
}

/**
 * Fetch a relayer's advertised metadata JSON. Convention: the JSON
 * document at the `metadataUri` URL contains at minimum an `endpoint`
 * field (the REST API base URL). Other fields (name, feeBps, region,
 * description) are optional and used purely for UI hints.
 *
 * Throws on network errors, non-JSON responses, or missing required
 * fields — the caller can catch and degrade gracefully.
 */
export async function fetchRelayerMetadata(uri: string): Promise<RelayerMetadata> {
  const res = await fetch(uri, { headers: { accept: "application/json" } });
  if (!res.ok) {
    throw new Error(`relayer metadata fetch ${res.status}`);
  }
  const json = (await res.json()) as Partial<RelayerMetadata>;
  if (typeof json.endpoint !== "string" || !json.endpoint) {
    throw new Error("relayer metadata missing 'endpoint' field");
  }
  return {
    endpoint: json.endpoint,
    tonAddress: typeof json.tonAddress === "string" ? json.tonAddress : undefined,
    name: typeof json.name === "string" ? json.name : undefined,
    feeBps: typeof json.feeBps === "number" ? json.feeBps : undefined,
    region: typeof json.region === "string" ? json.region : undefined,
    description: typeof json.description === "string" ? json.description : undefined,
  };
}

/**
 * Resolve a `RelayerEndpoint` to the relayer's TON address — the
 * address the on-chain pool will see in the SNARK's relayer field.
 *
 * Preference order:
 *   1. `metadata.tonAddress` if the relayer advertised one (friendly
 *      base64url, parsed via Address.parse).
 *   2. Reconstruct from on-chain `accountHash` assuming workchain=0
 *      (basechain — the norm for user wallets on TON).
 *
 * Returns `null` for the fallback (single-relayer mode) entry — that
 * one has no on-chain bond and isn't usable in the network-relayer
 * flow.
 */
export function resolveRelayerAddress(ep: RelayerEndpoint): Address | null {
  if (ep.metadata.tonAddress) {
    try {
      return Address.parse(ep.metadata.tonAddress);
    } catch {
      // Malformed metadata; fall through to accountHash reconstruction.
    }
  }
  if (!ep.registered) return null;
  const hashHex = ep.registered.accountHash.toString(16).padStart(64, "0");
  return new Address(0, Buffer.from(hashHex, "hex"));
}
